import { useState } from 'react';
import TopNavbar from '../../components/ui/TopNavbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import MeetingCard from './components/MeetingCard';
import AttendeePanel from './components/AttendeePanel';
import ActionItemTracker from './components/ActionItemTracker';
import DocumentAttachments from './components/DocumentAttachments';
import MeetingEditor from './components/MeetingEditor';
import MetricsSummary from './components/MetricsSummary';

const MonthlyReviewNotes = () => {
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showNewMeetingModal, setShowNewMeetingModal] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  const userRole = 'Admin';

  const [meetings, setMeetings] = useState([
    {
      id: 1,
      title: 'December 2025 Operations Review',
      date: '2025-12-10T10:00:00',
      duration: '2 hours',
      status: 'completed',
      organizer: 'Sarah Johnson',
      attendeesCount: 8,
      actionItemsCount: 5,
      content: `## Meeting Summary\n\nDiscussed Q4 operational performance and planning for 2026.\n\n### Key Decisions\n- Approved new supplier onboarding process\n- Increased inspection frequency for high-risk shipments\n- Implemented new cost tracking system\n\n### Operational Highlights\n- Total tonnage: 45,000 MT\n- On-time delivery: 94%\n- Quality compliance: 98%`,
      attendees: [
        { id: 1, name: 'Sarah Johnson', email: 'sarah.johnson@rva.com', role: 'organizer', status: 'accepted' },
        { id: 2, name: 'Michael Chen', email: 'michael.chen@rva.com', role: 'required', status: 'accepted' },
        { id: 3, name: 'Priya Sharma', email: 'priya.sharma@rnr.in', role: 'required', status: 'accepted' },
        { id: 4, name: 'David Wilson', email: 'david.wilson@rva.com', role: 'optional', status: 'accepted' }
      ],
      actionItems: [
        { id: 1, title: 'Update supplier qualification criteria', assignee: 'Michael Chen', dueDate: '2025-12-20', priority: 'high', status: 'in-progress', createdDate: '2025-12-10' },
        { id: 2, title: 'Review inspection protocols with lab partners', assignee: 'Sarah Johnson', dueDate: '2025-12-15', priority: 'medium', status: 'completed', createdDate: '2025-12-10' },
        { id: 3, title: 'Prepare cost analysis report for Q4', assignee: 'David Wilson', dueDate: '2025-12-18', priority: 'high', status: 'pending', createdDate: '2025-12-10' }
      ],
      documents: [
        { id: 1, name: 'Q4_Operations_Report.pdf', size: 2458624, type: 'application/pdf', uploadedBy: 'Sarah Johnson', uploadedDate: '2025-12-10' },
        { id: 2, name: 'Supplier_Performance_Analysis.xlsx', size: 1245632, type: 'application/vnd.ms-excel', uploadedBy: 'Michael Chen', uploadedDate: '2025-12-10' }
      ]
    },
    {
      id: 2,
      title: 'November 2025 Monthly Review',
      date: '2025-11-12T14:00:00',
      duration: '1.5 hours',
      status: 'completed',
      organizer: 'Sarah Johnson',
      attendeesCount: 7,
      actionItemsCount: 8,
      content: `## November Operations Review\n\n### Volume Summary\n- Total Tonnage: 38,500 MT\n- Number of Shipments: 12\n- Average Vessel Size: 3,208 MT\n\n### Financial Performance\n- Gross Fees: $192,500\n- Reimbursable Costs: $45,200\n- Net Revenue: $147,300`,
      attendees: [
        { id: 1, name: 'Sarah Johnson', email: 'sarah.johnson@rva.com', role: 'organizer', status: 'accepted' },
        { id: 2, name: 'Michael Chen', email: 'michael.chen@rva.com', role: 'required', status: 'accepted' },
        { id: 3, name: 'Priya Sharma', email: 'priya.sharma@rnr.in', role: 'required', status: 'declined' }
      ],
      actionItems: [
        { id: 1, title: 'Follow up on delayed shipment documentation', assignee: 'Michael Chen', dueDate: '2025-11-20', priority: 'high', status: 'completed', createdDate: '2025-11-12' },
        { id: 2, title: 'Negotiate better rates with inspection companies', assignee: 'Sarah Johnson', dueDate: '2025-11-25', priority: 'medium', status: 'completed', createdDate: '2025-11-12' }
      ],
      documents: [
        { id: 1, name: 'November_Meeting_Minutes.docx', size: 856432, type: 'application/msword', uploadedBy: 'Sarah Johnson', uploadedDate: '2025-11-12' }
      ]
    },
    {
      id: 3,
      title: 'January 2026 Planning Meeting',
      date: '2026-01-08T10:00:00',
      duration: '2 hours',
      status: 'scheduled',
      organizer: 'Sarah Johnson',
      attendeesCount: 9,
      actionItemsCount: 0,
      content: `## January 2026 Planning\n\n### Agenda\n1. Review 2025 performance\n2. Set 2026 targets\n3. Discuss new market opportunities\n4. Resource planning\n5. Technology initiatives`,
      attendees: [
        { id: 1, name: 'Sarah Johnson', email: 'sarah.johnson@rva.com', role: 'organizer', status: 'accepted' },
        { id: 2, name: 'Michael Chen', email: 'michael.chen@rva.com', role: 'required', status: 'tentative' },
        { id: 3, name: 'Priya Sharma', email: 'priya.sharma@rnr.in', role: 'required', status: 'invited' }
      ],
      actionItems: [],
      documents: []
    },
    {
      id: 4,
      title: 'October 2025 Operations Review',
      date: '2025-10-15T14:00:00',
      duration: '1.5 hours',
      status: 'completed',
      organizer: 'Sarah Johnson',
      attendeesCount: 6,
      actionItemsCount: 6,
      content: `## October Review\n\n### Key Highlights\n- Successfully completed 10 shipments\n- Resolved 3 critical quality issues\n- Implemented new document management system`,
      attendees: [
        { id: 1, name: 'Sarah Johnson', email: 'sarah.johnson@rva.com', role: 'organizer', status: 'accepted' },
        { id: 2, name: 'Michael Chen', email: 'michael.chen@rva.com', role: 'required', status: 'accepted' }
      ],
      actionItems: [
        { id: 1, title: 'Complete quality audit for Supplier A', assignee: 'Michael Chen', dueDate: '2025-10-25', priority: 'high', status: 'completed', createdDate: '2025-10-15' }
      ],
      documents: []
    }
  ]);

  const summaryMetrics = {
    totalTonnage: 45000,
    tonnageChange: 12,
    activeShipments: 15,
    shipmentsChange: 8,
    grossFees: 225000,
    feesChange: 15,
    openIssues: 7,
    issuesChange: -3
  };

  const monthOptions = [
    { value: '', label: 'All Months' },
    { value: '2026-01', label: 'January 2026' },
    { value: '2025-12', label: 'December 2025' },
    { value: '2025-11', label: 'November 2025' },
    { value: '2025-10', label: 'October 2025' },
    { value: '2025-09', label: 'September 2025' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'completed', label: 'Completed' },
    { value: 'draft', label: 'Draft' }
  ];

  const filteredMeetings = meetings?.filter(meeting => {
    const matchesSearch = meeting?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         meeting?.content?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesMonth = !filterMonth || meeting?.date?.startsWith(filterMonth);
    const matchesStatus = !filterStatus || meeting?.status === filterStatus;
    return matchesSearch && matchesMonth && matchesStatus;
  });

  const handleSelectMeeting = (meeting) => {
    setSelectedMeeting(meeting);
  };

  const handleUpdateMeetingContent = (content) => {
    if (selectedMeeting) {
      const updatedMeetings = meetings?.map(m =>
        m?.id === selectedMeeting?.id ? { ...m, content } : m
      );
      setMeetings(updatedMeetings);
      setSelectedMeeting({ ...selectedMeeting, content });
    }
  };

  const handleNewMeeting = () => {
    setSelectedMeeting(null);
    setShowEditor(true);
  };

  const handleEditMeeting = (meeting) => {
    setSelectedMeeting(meeting);
    setShowEditor(true);
  };

  const handleSaveMeeting = (meetingData) => {
    console.log('Saving meeting:', meetingData);
    // TODO: Implement actual save with Supabase
    alert('✅ Meeting notes saved successfully!');
    setShowEditor(false);
  };

  const handleDeleteMeeting = (meetingId) => {
    if (confirm('Are you sure you want to delete these meeting notes?')) {
      console.log('Deleting meeting:', meetingId);
      // TODO: Implement deletion
      alert('✅ Meeting notes deleted!');
    }
  };

  const handleAddAttendee = (meetingId, attendee) => {
    console.log('Adding attendee:', meetingId, attendee);
    // TODO: Implement attendee addition
    alert(`✅ ${attendee} added to meeting!`);
  };

  const handleRemoveAttendee = (meetingId, attendeeId) => {
    console.log('Removing attendee:', meetingId, attendeeId);
    // TODO: Implement attendee removal
    alert('✅ Attendee removed!');
  };

  const handleAddActionItem = (meetingId, actionItem) => {
    console.log('Adding action item:', meetingId, actionItem);
    // TODO: Implement action item addition
    alert('✅ Action item added!');
  };

  const handleCompleteActionItem = (actionItemId) => {
    console.log('Completing action item:', actionItemId);
    // TODO: Implement completion
    alert('✅ Action item marked as complete!');
  };

  const handleUpdateActionItem = (actionItemId, updates) => {
    console.log('Updating action item:', actionItemId, updates);
    // TODO: Implement action item update
    alert('✅ Action item updated!');
  };

  const handleDeleteActionItem = (actionItemId) => {
    console.log('Deleting action item:', actionItemId);
    // TODO: Implement action item deletion
    alert('✅ Action item deleted!');
  };

  const handleAddDocument = (meetingId, document) => {
    console.log('Adding document:', meetingId, document);
    // TODO: Implement document addition
    alert('✅ Document added!');
  };

  const handleRemoveDocument = (meetingId, documentId) => {
    console.log('Removing document:', meetingId, documentId);
    // TODO: Implement document removal
    alert('✅ Document removed!');
  };

  const handleExportMeeting = () => {
    if (selectedMeeting) {
      console.log('Exporting meeting:', selectedMeeting.id);
      // TODO: Implement export
      alert('✅ Meeting notes exported as PDF!');
    }
  };

  const handleAttachDocument = (meetingId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e?.target?.files);
      console.log('Attaching documents:', meetingId, files);
      // TODO: Implement document attachment
      alert(`✅ ${files?.length} document(s) attached!`);
    };
    input?.click();
  };

  const handleExportNotes = (meetingId) => {
    console.log('Exporting notes:', meetingId);
    // TODO: Implement export
    alert('✅ Meeting notes exported as PDF!');
  };

  const canEdit = userRole === 'Admin' || userRole === 'Ops' || 
                  (selectedMeeting && selectedMeeting?.organizer === 'Current User');

  return (
    <div className="flex flex-col h-screen bg-background">
      <TopNavbar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Page Header Section - Restored */}
        <div className="sticky top-[65px] z-30 bg-background border-b border-border">
          <div className="p-6 pb-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Monthly Review Notes
                </h1>
                <p className="text-muted-foreground">
                  Meeting documentation and operational review system
                </p>
              </div>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setShowNewMeetingModal(true)}
              >
                New Meeting
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <MetricsSummary metrics={summaryMetrics} />

            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 lg:col-span-3">
                <div className="bg-card border border-border rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Filter" size={18} />
                    Filters
                  </h3>
                  <div className="space-y-3">
                    <Input
                      type="search"
                      placeholder="Search meetings..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                    />
                    <Select
                      label="Month"
                      options={monthOptions}
                      value={filterMonth}
                      onChange={setFilterMonth}
                    />
                    <Select
                      label="Status"
                      options={statusOptions}
                      value={filterStatus}
                      onChange={setFilterStatus}
                    />
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Calendar" size={18} />
                    Meetings ({filteredMeetings?.length})
                  </h3>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto custom-scrollbar">
                    {filteredMeetings?.map(meeting => (
                      <MeetingCard
                        key={meeting?.id}
                        meeting={meeting}
                        onSelect={handleSelectMeeting}
                        isSelected={selectedMeeting?.id === meeting?.id}
                        onEdit={(m) => console.log('Edit meeting:', m)}
                        onDelete={(m) => console.log('Delete meeting:', m)}
                        userRole={userRole}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="col-span-12 lg:col-span-6">
                {selectedMeeting ? (
                  <div className="space-y-4">
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h2 className="text-2xl font-bold text-foreground mb-2">
                            {selectedMeeting?.title}
                          </h2>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Icon name="Calendar" size={16} />
                              <span>
                                {new Date(selectedMeeting.date)?.toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Icon name="Clock" size={16} />
                              <span>{selectedMeeting?.duration}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Icon name="User" size={16} />
                              <span>Organized by {selectedMeeting?.organizer}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Download"
                            onClick={handleExportMeeting}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Share2"
                            onClick={() => console.log('Share meeting')}
                          />
                        </div>
                      </div>
                    </div>

                    <MeetingEditor
                      content={selectedMeeting?.content}
                      onChange={handleUpdateMeetingContent}
                      canEdit={canEdit}
                    />
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-lg p-12 text-center">
                    <Icon name="FileText" size={64} className="mx-auto mb-4 text-muted-foreground opacity-30" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      No Meeting Selected
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Select a meeting from the list to view and edit notes
                    </p>
                    <Button
                      variant="default"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => setShowNewMeetingModal(true)}
                    >
                      Create New Meeting
                    </Button>
                  </div>
                )}
              </div>

              <div className="col-span-12 lg:col-span-3">
                {selectedMeeting ? (
                  <div className="space-y-4">
                    <AttendeePanel
                      attendees={selectedMeeting?.attendees}
                      onAddAttendee={handleAddAttendee}
                      onRemoveAttendee={handleRemoveAttendee}
                      canEdit={canEdit}
                    />

                    <ActionItemTracker
                      actionItems={selectedMeeting?.actionItems}
                      onAddItem={handleAddActionItem}
                      onUpdateItem={handleUpdateActionItem}
                      onDeleteItem={handleDeleteActionItem}
                      canEdit={canEdit}
                    />

                    <DocumentAttachments
                      documents={selectedMeeting?.documents}
                      onAddDocument={handleAddDocument}
                      onRemoveDocument={handleRemoveDocument}
                      canEdit={canEdit}
                    />
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-lg p-8 text-center">
                    <Icon name="Info" size={48} className="mx-auto mb-3 text-muted-foreground opacity-30" />
                    <p className="text-sm text-muted-foreground">
                      Meeting details will appear here when you select a meeting
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyReviewNotes;